import { useState } from "react";

import { removeAccents } from "utils/utils";

import { SelectedLandlord } from "js/pages/Tenants/types";
import {
  PROPERTY_FIELD_KEYS,
  PropertyFieldKey,
  arePropertyFieldsValid,
  getPropertyFieldError,
} from "js/utils/propertyValidation";

export interface PropertyFormData {
  uso: string;
  alias: string;
  rental_price: string;
  maintenance_price: string;
  street: string;
  exterior_number: string;
  interior_number: string;
  zip_code: string;
  colonia: string;
  municipality: string;
  city: string;
  state: string;
}

type TouchedFields = Partial<Record<PropertyFieldKey, boolean>>;

const initialFormData: PropertyFormData = {
  uso: "",
  alias: "",
  rental_price: "",
  maintenance_price: "",
  street: "",
  exterior_number: "",
  interior_number: "",
  zip_code: "",
  colonia: "",
  municipality: "",
  city: "",
  state: "",
};

export const usePropertyForm = (isEditMode: boolean = false) => {
  const [formData, setFormData] = useState<PropertyFormData>(initialFormData);
  const [originalFormData, setOriginalFormData] =
    useState<PropertyFormData>(initialFormData);
  const [selectedLandlord, setSelectedLandlord] = useState<SelectedLandlord>(
    {},
  );
  const [originalLandlord, setOriginalLandlord] = useState<SelectedLandlord>(
    {},
  );
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});

  const updateFormData = (
    updates: Partial<PropertyFormData>,
    isInitialLoad = false,
  ) => {
    setFormData((prev) => {
      const updated = { ...prev, ...updates };
      Object.keys(updates).forEach((key) => {
        if (updated[key as keyof PropertyFormData]) {
          const normalizedValue = removeAccents(
            String(updated[key as keyof PropertyFormData]),
          );
          (updated as any)[key] = normalizedValue;
        }
      });
      return updated;
    });

    if (isInitialLoad) {
      setOriginalFormData((prev) => {
        const updated = { ...prev, ...updates };
        Object.keys(updates).forEach((key) => {
          if (updated[key as keyof PropertyFormData]) {
            const normalizedValue = removeAccents(
              String(updated[key as keyof PropertyFormData]),
            );
            (updated as any)[key] = normalizedValue;
          }
        });
        return updated;
      });
    }
  };

  const setFieldTouched = (field: PropertyFieldKey) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setOriginalFormData(initialFormData);
    setSelectedLandlord({});
    setOriginalLandlord({});
    setTouchedFields({});
  };

  const validateAllFields = () => {
    const touched: TouchedFields = {};
    PROPERTY_FIELD_KEYS.forEach((field) => {
      touched[field] = true;
    });
    setTouchedFields(touched);
  };

  const validateStep1 = () => {
    return arePropertyFieldsValid(formData, { requireUso: !isEditMode });
  };

  const hasFormBeenModified = () => {
    const formChanged =
      formData.uso !== originalFormData.uso ||
      formData.alias !== originalFormData.alias ||
      formData.rental_price !== originalFormData.rental_price ||
      formData.maintenance_price !== originalFormData.maintenance_price ||
      formData.street !== originalFormData.street ||
      formData.exterior_number !== originalFormData.exterior_number ||
      formData.interior_number !== originalFormData.interior_number ||
      formData.zip_code !== originalFormData.zip_code ||
      formData.colonia !== originalFormData.colonia ||
      formData.municipality !== originalFormData.municipality ||
      formData.city !== originalFormData.city ||
      formData.state !== originalFormData.state;

    const landlordChanged =
      JSON.stringify(selectedLandlord) !== JSON.stringify(originalLandlord);

    return formChanged || landlordChanged;
  };

  const getFieldError = (field: PropertyFieldKey): string | undefined => {
    if (!touchedFields[field]) return undefined;

    return getPropertyFieldError(field, formData, {
      requireUso: !isEditMode,
    });
  };

  const setSelectedLandlordWithOriginal = (
    landlord: SelectedLandlord,
    isInitialLoad = false,
  ) => {
    setSelectedLandlord(landlord);
    if (isInitialLoad) {
      setOriginalLandlord(landlord);
    }
  };

  return {
    formData,
    updateFormData,
    selectedLandlord,
    setSelectedLandlord: setSelectedLandlordWithOriginal,
    resetForm,
    validateStep1,
    hasFormBeenModified,
    getFieldError,
    setFieldTouched,
    touchedFields,
    validateAllFields,
  };
};
